import { useCallback } from 'react';

import { useShowConsumerFamily } from '@givebacks/api/backer';
import { Avatar, Box, Button, Divider, Modal, Skeleton, Stack, Text, ThemeIcon } from 'ui';
import { IconAdd } from 'ui/Icons';

import { ClickableOptionsCard } from '../../../../Cards/ClickableOptionsCard/ClickableOptionsCard';
import {
  ModalTitleWithBackButton,
  useDirectorySettingsModalProvider,
} from '../DirectorySettingsModalProvider';

const EditFamilyButton = () => {
  const { openModalById } = useDirectorySettingsModalProvider({});

  return (
    <Modal.AffixedFooter>
      <Button fullWidth onClick={() => openModalById('family')}>
        Edit Family
      </Button>
    </Modal.AffixedFooter>
  );
};

const ManageFamilyMembersContent = () => {
  const { meta, openModalById, setMeta } = useDirectorySettingsModalProvider({
    id: 'members',
  });
  const { data: family, isLoading } = useShowConsumerFamily({
    familyId: meta.familyId ?? '',
    include: 'backers,children',
  });

  const handleEditAdult = useCallback(
    (backerId: string) => {
      setMeta((prev) => ({
        ...prev,
        backerId,
      }));
      openModalById('backer-profile');
    },
    [setMeta, openModalById]
  );

  const handleEditChild = useCallback(
    (childId: string) => {
      setMeta((prev) => ({
        ...prev,
        childId,
      }));
      openModalById('child-profile');
    },
    [setMeta, openModalById]
  );

  return (
    <Stack gap="lg">
      <Text fz="xs" c="gray">
        Manage family members you’d like to display in your organizations’ directories.
      </Text>
      <Skeleton visible={isLoading}>
        <Text fw={600} fz="lg">
          {family?.name} Family
        </Text>
      </Skeleton>

      <Stack gap={4}>
        <Text fw={600} fz="lg">
          Adults
        </Text>
        <Box>
          {family?.backers?.map((adult) => (
            <Skeleton key={adult.uuid} visible={isLoading}>
              <ClickableOptionsCard.Row
                leftSection={
                  <Avatar
                    size={48}
                    color="cobalt"
                    name={`${adult.first_name} ${adult.last_name}`}
                    ml={-16}
                  />
                }
                title={`${adult.first_name} ${adult.last_name}`}
                subtitle={adult.email}
                onClick={() => handleEditAdult(adult.uuid)}
              />
              <Divider color="gray.0" size="sm" />
            </Skeleton>
          ))}
          <ClickableOptionsCard.Row
            leftSection={
              <ThemeIcon variant="transparent" color="cobalt">
                <IconAdd />
              </ThemeIcon>
            }
            title="Add Adult"
            onClick={() => openModalById('backer-profile')}
            rightSection={<></>}
          />
        </Box>
      </Stack>
      <Stack gap={4}>
        <Text fw={600} fz="lg">
          Children
        </Text>
        <Box>
          {family?.children?.map((child) => (
            <Skeleton key={child.uuid} visible={isLoading}>
              <ClickableOptionsCard.Row
                leftSection={
                  <Avatar
                    size={48}
                    color="cobalt"
                    name={`${child.first_name} ${child.last_name}`}
                    ml={-16}
                  />
                }
                title={`${child.first_name} ${child.last_name}`}
                subtitle={child.causes?.map((cause) => cause.name).join(', ') ?? ''}
                onClick={() => handleEditChild(child.uuid)}
              />
              <Divider color="gray.0" size="sm" />
            </Skeleton>
          ))}
          <ClickableOptionsCard.Row
            leftSection={
              <ThemeIcon variant="transparent" color="cobalt">
                <IconAdd />
              </ThemeIcon>
            }
            title="Add Child"
            onClick={() => openModalById('child-profile')}
            rightSection={<></>}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

const ManageFamilyMembersModal = () => {
  const { opened, close } = useDirectorySettingsModalProvider({
    id: 'members',
    onOpened: ({ setMeta }) => {
      setMeta((prev) => ({
        ...prev,
        backerId: undefined,
        childId: undefined,
      }));
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Manage Family</ModalTitleWithBackButton>}
    >
      <ManageFamilyMembersContent />
      <EditFamilyButton />
    </Modal>
  );
};

export { ManageFamilyMembersModal };
